import React from 'react';

/**
 * Shared PasswordTooltip Component
 * What: Reusable password requirements tooltip with visual indicators
 * Why: Provides consistent password validation feedback across the application
 * Where: shared/components/ui/PasswordTooltip.jsx
 * 
 * Used in:
 *   - RegisterPage (src/features/auth/pages/RegisterPage.js)
 *   - ResetPasswordPage (src/features/auth/pages/ResetPasswordPage.js)
 *   - ChangePasswordModal (src/features/account/components/ChangePasswordModal.jsx)
 */
export const PasswordTooltip = ({ passwordStrength, isVisible }) => {
  if (!isVisible) return null;

  const getRequirementColor = (isValid) =>
    isValid ? 'text-success-600' : 'text-error-500';

  const getRequirementDot = (isValid) =>
    isValid ? 'bg-success-500' : 'bg-error-500';

  return (
    <>
      {/* Desktop: floating tooltip */}
      <div
        className="hidden sm:block absolute z-10 password-tooltip
          bg-white border border-gray-200 rounded-lg shadow-lg p-3
          w-max max-w-xs md:max-w-sm sm:max-w-[calc(100vw-2rem)]
          md:left-1/2 md:top-full md:mt-2 md:-translate-x-1/2 md:right-auto md:mr-0
          lg:left-auto lg:top-0 lg:mt-0 lg:right-full lg:mr-2 lg:translate-x-0">
        <div className="text-xs text-gray-600 mb-2 font-medium">Password Requirements:</div>
        <div className="space-y-1">
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasUppercase)}`}>
            <span className={`w-1 h-1 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasUppercase)}`}></span>
            <span>1 uppercase letter</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasNumber)}`}>
            <span className={`w-1 h-1 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasNumber)}`}></span>
            <span>1 number</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasSpecial)}`}>
            <span className={`w-1 h-1 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasSpecial)}`}></span>
            <span>1 special character</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasMinLength)}`}>
            <span className={`w-1 h-1 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasMinLength)}`}></span>
            <span>8 characters</span>
          </div>
        </div>
        <div className="hidden lg:block absolute -right-1 top-4 w-2 h-2 bg-white border-r border-t border-gray-200 transform rotate-45"></div>
      </div>

      {/* Mobile: inline block */}
      <div className="sm:hidden mt-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</div>
        <div className="space-y-1">
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasUppercase)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasUppercase)}`}></span>
            <span>1 uppercase letter</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasNumber)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasNumber)}`}></span>
            <span>1 number</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasSpecial)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasSpecial)}`}></span>
            <span>1 special character</span>
          </div>
          <div className={`flex items-center text-xs ${getRequirementColor(passwordStrength.hasMinLength)}`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-2 ${getRequirementDot(passwordStrength.hasMinLength)}`}></span>
            <span>8 characters</span>
          </div>
        </div>
      </div>
    </>
  );
};